import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import { Subscription, of, throwError } from "rxjs";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Actions, ofType } from "@ngrx/effects";
import { MessageService } from "primeng/api";
import { saveAs } from 'file-saver';
import {
  ImportForecastTemplate,
  ImportForecastTemplateSuccess,
  AppActionTypes,
  ImportForecastTemplateFailed,
  ImportForecastUploadFile,
  ImportForecastUploadFileFailed,
  ImportForecastUploadFileProgress,
  ImportForecastUploadFileSuccess,
  CheckImportForecastFileExists,
  ImportForecastViewLog, CheckImportForecastFileExistsSuccess, CheckImportForecastFileExistsFailed
} from 'src/app/store/actions';

@Component({
  standalone: false,
  selector: 'wfm-esp-imported-forecasts-dialog',
  templateUrl: './imported-forecasts-dialog.component.html',
  styleUrls: ['./imported-forecasts-dialog.component.scss'],
  providers: [MessageService]
})
export class ImportedForecastsDialogComponent implements OnInit, OnDestroy {

  protected _isLoading = false;
  protected _enableCancel = true;
  protected subscriptionList: Array<Subscription> = [];
  readonly _nameOfTemplateFile = "forecast-import-template.csv";
  readonly _blobType = 'text/csv';
  readonly _maxFileSize = 200000000;
  readonly _noFileChosen = this.translate.instant("import.forecast.dialog.file.choose.empty");
  readonly _fileExtensionIsNotValid = this.translate.instant("import.forecast.dialog.file.upload.validation");
  readonly _fileSizeNotIsNotValid = this.translate.instant("import.forecast.dialog.file.upload.validation.size");
  readonly _fileNameExists = this.translate.instant("import.forecast.dialog.same.file.exists");
  readonly _fileNameCheckFailed = this.translate.instant("import.forecast.dialog.same.file.check.failed");

  _showError = false;
  _messageSelectedFile = "";
  _errorMessage = "";
  _chkboxErrorMessage = "";
  _enableAccept = false;
  _isUploadingFile = false;
  _percentageLoaded : number = 0;
  _fileToUpload = null;
  _showCheckBox = false;
  isConfirmed = false;

  // EXPERIMENTAL (ESP-18588): true only while the native OS file picker is open.
  // While true the Choose File button's accessible name is blanked (title
  // removed + label span aria-hidden in the template) so JAWS's window-activation
  // snapshot - taken when the picker closes, before our (change) handler runs -
  // has no control name to read. Restored shortly after focus returns.
  _silenceChooseFileName = false;
  private _pickerBlurHandler: (() => void) | null = null;
  private _pickerFocusHandler: (() => void) | null = null;
  private _restoreNameTimeout: ReturnType<typeof setTimeout> | null = null;

  // The visible error text (role="alert") and the file-exists overwrite
  // checkbox - rendered by renderErrorForAnnouncement().
  @ViewChild('errorAlert') errorAlert?: ElementRef<HTMLElement>;
  @ViewChild('errorChkbox') errorChkbox?: ElementRef<HTMLElement>;

  constructor(public activeModal: NgbActiveModal, private store: Store<any>,
              private action$: Actions, private messageService: MessageService,
              private translate: TranslateService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initHandlers();
    this._messageSelectedFile = this._noFileChosen;
  }

  close(result) {
    this.activeModal.close(result);
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  download() {
    let payload = {};
    this.store.dispatch(new ImportForecastTemplate(payload));
  }

  downloadSuccess(action: ImportForecastTemplateSuccess) {
    const blob = new Blob([(action.payload)], { type: this._blobType });
    this.saveAs(blob, this._nameOfTemplateFile);
  }

  // Thin wrapper around the file-saver module default export so tests can
  // spy on it. Strict ESM under esbuild forbids mutating the module-level
  // `saveAs` binding (spyOn(FileSaver, 'saveAs') fails with "not declared
  // writable"); a method on the component is a writable property.
  protected saveAs(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  downloadFailed(action: ImportForecastTemplateFailed) {
    let failMsg = this.translate.instant("import.forecast.dialog.template.failure");
    this.messageService.add({ severity: "error", detail: failMsg });
  }

  enableUploadButton(event){
    this._enableAccept = event.target.checked;
    this.isConfirmed = event.target.checked;
  }

  onFileChange(event) {
    let fileNameToUpload: string, typeOfFileToUpload: string;
    this._enableAccept = false;
    this._showError = false;
    this._showCheckBox = false;

    this._fileToUpload = event.target.files[0];
    if (!this._fileToUpload) {
      this._messageSelectedFile = this._noFileChosen;
      return;
    }

    // 1. Verify type of the file is CSV.
    fileNameToUpload = this._fileToUpload.name;
    typeOfFileToUpload = fileNameToUpload.substring(fileNameToUpload.lastIndexOf('.') + 1);
    if (typeOfFileToUpload.toLowerCase() !== 'csv') {
      this._showError = true;
      this._messageSelectedFile = this._noFileChosen;
      this._fileToUpload = null;
      this._errorMessage = this._fileExtensionIsNotValid;
      this.renderErrorForAnnouncement();
      return;
    }

    // 2. Verify size of the file.
    if (this._fileToUpload.size > this._maxFileSize) {
      this._showError = true;
      this._messageSelectedFile = this._noFileChosen;
      this._fileToUpload = null;
      this._errorMessage = this._fileSizeNotIsNotValid;
      this.renderErrorForAnnouncement();
      return;
    }

    // 3. Check if the file name already exists
    this._isLoading = true;
    this._messageSelectedFile = fileNameToUpload;
    this.checkFileExists(fileNameToUpload);
  }

  checkFileExists(importForecastName) {
    let payload = { "importForecastName": importForecastName };
    this.store.dispatch(new CheckImportForecastFileExists(payload));
  }

  checkFileExistsSuccess(action: CheckImportForecastFileExistsSuccess) {
    this._enableAccept = true;
    this._isLoading = false;
    this._showError = false;
    this._showCheckBox = false;
    this._errorMessage = "";
  }

  checkFileExistsFailed(action: CheckImportForecastFileExistsFailed) {
    if (action.payload.errorStatus) {
      this._errorMessage = this._fileNameCheckFailed;
      this._showCheckBox = false;
    } else {
      this._chkboxErrorMessage = this._fileNameExists;
      this._showCheckBox = true;
    }
    this._enableAccept = false;
    this._isLoading = false;
    this._showError = true;
    this.renderErrorForAnnouncement();
  }

  // On a validation/upload failure the error is (1) programmatically associated
  // with the Choose File control via aria-describedby/aria-invalid and (2)
  // exposed to assistive technology through a role="alert" region
  // (#msgErrorValidation, or #chkboxErrorMessage for the file-exists case).
  //
  // We deliberately do NOT move keyboard focus onto that region. A role="alert"
  // region already announces its content exactly ONCE the moment it appears.
  // Moving focus onto that same element makes the screen reader read it AGAIN
  // (once as the live-region update, once as the newly-focused element) - that
  // is the "error announced twice" defect. Calling detectChanges() synchronously
  // renders the just-unhidden region so its single announcement fires.
  private renderErrorForAnnouncement() {
    this.cdr.detectChanges();
  }

  // EXPERIMENTAL a11y mitigation (ESP-18588). The leading readout QA hears -
  // "browser tab + page title + dialog name + Choose File control name" - is a
  // single JAWS "window-activation" announcement fired when the native OS file
  // picker window closes and Chrome regains the foreground. JAWS snapshots the
  // window AND the currently focused control (this Choose File button, where the
  // picker returns focus) at that instant - which is BEFORE the input (change)
  // event runs our validation, so no focus move / ARIA change made in
  // onFileChange() can influence that snapshot.
  //
  // The only lever left is to make the button have NO accessible name at the
  // moment of the snapshot. We blank it (title removed + label span aria-hidden,
  // see template) just before opening the picker, and restore it shortly AFTER
  // the window regains focus - deferred so the snapshot still sees the blank
  // name. Crucially we only arm the restore once the window has actually blurred
  // (the picker took the foreground), so a stray 'focus' fired around
  // input.click() cannot restore the name while the picker is still open. We
  // restore on the window 'focus' event (which fires whether the user picked a
  // file OR cancelled) so the button never stays unnamed.
  onChooseFileClick(input: HTMLInputElement) {
    this.clearChooseFileNameRestore();

    // Clear any error left over from a previous attempt BEFORE the picker opens.
    // Otherwise, when the picker closes, JAWS's window-activation snapshot reads
    // the still-visible old error text AND then renderErrorForAnnouncement()
    // re-renders the (new) error region so it is announced again - i.e. the
    // error is announced twice. With the stale error cleared, there is nothing
    // to read on window-activation, so the error is announced exactly once (when
    // the region is rendered after validation).
    this._showError = false;
    this._showCheckBox = false;

    this._silenceChooseFileName = true;
    this.cdr.detectChanges();

    // Keep the name blanked across the ENTIRE picker lifecycle. We arm the
    // restore-on-focus handler only AFTER the window has actually blurred (i.e.
    // the OS picker took the foreground). Otherwise a stray 'focus' event fired
    // synchronously around input.click() would run the restore while the picker
    // is still open, so JAWS's window-activation snapshot (taken when the picker
    // later closes) would read the Choose File name again - the exact symptom QA
    // still reports.
    this._pickerBlurHandler = () => {
      if (this._pickerBlurHandler) {
        window.removeEventListener('blur', this._pickerBlurHandler);
        this._pickerBlurHandler = null;
      }
      this._pickerFocusHandler = () => {
        if (this._pickerFocusHandler) {
          window.removeEventListener('focus', this._pickerFocusHandler);
          this._pickerFocusHandler = null;
        }
        // Defer the restore so JAWS's window-activation snapshot (taken as the
        // window regains focus) still sees the blank name.
        this._restoreNameTimeout = setTimeout(() => {
          this._restoreNameTimeout = null;
          this._silenceChooseFileName = false;
          this.cdr.detectChanges();
        }, 1500);
      };
      window.addEventListener('focus', this._pickerFocusHandler);
    };
    window.addEventListener('blur', this._pickerBlurHandler);

    input.click();
  }

  private clearChooseFileNameRestore() {
    if (this._pickerBlurHandler) {
      window.removeEventListener('blur', this._pickerBlurHandler);
      this._pickerBlurHandler = null;
    }
    if (this._pickerFocusHandler) {
      window.removeEventListener('focus', this._pickerFocusHandler);
      this._pickerFocusHandler = null;
    }
    if (this._restoreNameTimeout) {
      clearTimeout(this._restoreNameTimeout);
      this._restoreNameTimeout = null;
    }
    this._silenceChooseFileName = false;
  }

  uploadFile() {
    if (!this._fileToUpload) {
      console.log("fileToUpload is not valid.");
      return;
    }
    this._percentageLoaded = 1;
    this._isUploadingFile = true; // start the upload of the file, disable buttons.
    const formData = new FormData();
    formData.append("file", this._fileToUpload);
    this.store.dispatch(new ImportForecastUploadFile(formData));
  }

  uploadFileSuccess(action: ImportForecastUploadFileSuccess) {

    this._messageSelectedFile = this._noFileChosen;
    this._enableAccept = false;
    this._isUploadingFile = false;

    this.close('uploadFileSuccess');
  }

  uploadFileFailed(action: ImportForecastUploadFileFailed) {
    this._errorMessage = this.translate.instant("import.forecast.dialog.file.upload.failed");
    this._isUploadingFile = false;
    this._messageSelectedFile = this._noFileChosen;
    this._enableAccept = false;
    this._showError = true;
    this.renderErrorForAnnouncement();
  }

  uploadFileProgress(action: ImportForecastUploadFileProgress) {
    if (action.payload.percentageLoaded) {
      this._percentageLoaded = action.payload.percentageLoaded;
    }
  }

  initHandlers() {
    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ImportForecastTemplateSuccess))
        .subscribe(this.downloadSuccess.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ImportForecastTemplateFailed))
        .subscribe(this.downloadFailed.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ImportForecastUploadFileSuccess))
        .subscribe(this.uploadFileSuccess.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ImportForecastUploadFileFailed))
        .subscribe(this.uploadFileFailed.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ImportForecastUploadFileProgress))
        .subscribe(this.uploadFileProgress.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.CheckImportForecastFileExistsSuccess))
        .subscribe(this.checkFileExistsSuccess.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.CheckImportForecastFileExistsFailed))
        .subscribe(this.checkFileExistsFailed.bind(this))
    );
  }

  protected addToSubscriptionList(newSubscription: Subscription) {
    this.subscriptionList.push(newSubscription);
  }

  private clearSubscriptionList() {
    if (this.subscriptionList.length > 0) {
      this.subscriptionList.forEach(subscriptionItem => subscriptionItem.unsubscribe());
      this.subscriptionList = null;
    }
  }

  public ngOnDestroy() {
    this.clearChooseFileNameRestore();
    this.clearSubscriptionList();
  }

}
